import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bot, CheckCircle2, PencilLine } from "lucide-react";
import { toast } from "sonner";

export const AiTrustCard = ({ companyId }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!companyId) return;
    apiClient
      .get(`/companies/${companyId}/ai-trust`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const pct = Math.round((data.rate || 0) * 100);
  const canEnable = data.recommendation || data.auto_enabled;

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/ai-trust/auto`, {
        enabled: !data.auto_enabled,
      });
      toast.success(r.data.auto_enabled ? "🤖 Autopilot aktiviert — KI-Belege werden automatisch gebucht" : "Autopilot deaktiviert — Buchhalter prüft wieder alles");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Umschalten fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5" data-testid="dashboard-ai-trust-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className={`h-8 w-8 rounded-lg text-white flex items-center justify-center ${data.auto_enabled ? "bg-emerald-600" : "bg-violet-600"}`}>
            <Bot className="h-4 w-4" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-sm">
              KI-Autopilot {data.auto_enabled ? "· AKTIV" : "· Lernmodus"}
            </h3>
            <p className="text-[11px] text-slate-500">
              {data.auto_enabled
                ? "KI-erfasste Belege werden automatisch gebucht (Chat, WhatsApp, E-Mail)"
                : "Der Buchhalter prüft jede KI-Buchung — das System misst die Trefferquote"}
            </p>
          </div>
        </div>
        {data.is_owner && (
          <Button
            size="sm"
            variant={data.auto_enabled ? "outline" : "default"}
            disabled={busy || (!data.auto_enabled && !canEnable)}
            onClick={toggle}
            className={!data.auto_enabled && canEnable ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            data-testid="ai-trust-toggle"
          >
            {data.auto_enabled ? "Autopilot ausschalten" : "Autopilot einschalten"}
          </Button>
        )}
      </div>

      <div className="mt-4 flex items-center gap-4 flex-wrap">
        <div>
          <div className="text-2xl font-bold text-slate-900" data-testid="ai-trust-rate">
            {data.total_reviews > 0 ? `${pct}%` : "—"}
          </div>
          <div className="text-[11px] text-slate-500">Trefferquote (unverändert bestätigt)</div>
        </div>
        <div className="flex items-center gap-1.5 text-[13px] text-emerald-700">
          <CheckCircle2 className="h-4 w-4" /> {data.confirmed_unchanged} bestätigt
        </div>
        <div className="flex items-center gap-1.5 text-[13px] text-amber-700">
          <PencilLine className="h-4 w-4" /> {data.corrected} korrigiert
        </div>
        <div className="text-[11px] text-slate-400">
          von {data.total_reviews} Freigaben (letzte 200)
        </div>
      </div>

      <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${pct >= 90 ? "bg-emerald-500" : "bg-violet-500"}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>

      {!data.auto_enabled && (
        <p className="mt-2 text-[11px] text-slate-500" data-testid="ai-trust-hint">
          {data.recommendation
            ? "✅ Empfehlung erreicht: ≥90% Trefferquote bei ≥20 Freigaben — du kannst den Autopiloten einschalten."
            : `Automatik wird empfohlen ab ≥${Math.round(data.threshold_rate * 100)}% Trefferquote bei mindestens ${data.min_sample} Freigaben (aktuell ${data.total_reviews}).`}
        </p>
      )}
    </Card>
  );
};
